import type { IconProps } from '@phosphor-icons/react'
import { forwardRef } from 'react'
import { cn } from './primitives'

/**
 * Aliens Materials marks (24-unit grid), drawn inline like ShipmentTypeIcon / FinanceIcons so the colour says what it
 * is before the word does: the shop bag, a new order, and one mark per catalog category. Phosphor-compatible
 * components (size / weight / className) so they slot into PageHeader, KpiCard and the navigation unchanged.
 */
export type MaterialsIconKey = 'store' | 'newOrder' | 'flyer' | 'sticker' | 'box' | 'tape' | 'other' | 'settings'

export const MATERIALS_ICON_TONE: Record<MaterialsIconKey, { fg: string; bg: string }> = {
  store: { fg: '#0d9488', bg: '#e0f5f2' },
  newOrder: { fg: '#16a34a', bg: '#e7f6ef' },
  flyer: { fg: '#2563eb', bg: '#e6eefc' },
  sticker: { fg: '#db2777', bg: '#fde8f1' },
  box: { fg: '#b45309', bg: '#fbefdc' },
  tape: { fg: '#7c3aed', bg: '#efe8fd' },
  other: { fg: '#64748b', bg: '#eef1f5' },
  settings: { fg: '#475569', bg: '#eef1f5' },
}

const S = { strokeWidth: 1.8, strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const }

function paths(key: MaterialsIconKey, fg: string) {
  switch (key) {
    case 'store':
      // a shopping bag with a small box inside: the packaging shop
      return (<>
        <path d="M4.5 8h15l-1.1 12.2a1.6 1.6 0 0 1-1.6 1.4H7.2a1.6 1.6 0 0 1-1.6-1.4L4.5 8Z" fill={fg} opacity=".18" stroke={fg} {...S} />
        <path d="M8.5 10V6.5a3.5 3.5 0 0 1 7 0V10" stroke={fg} {...S} />
        <path d="m9 14.6 3-1.5 3 1.5v3l-3 1.5-3-1.5v-3Z" fill={fg} stroke={fg} strokeWidth="1.2" strokeLinejoin="round" />
        <path d="m9 14.6 3 1.5 3-1.5M12 16.1v3" stroke="#fff" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" />
      </>)
    case 'newOrder':
      // a bag with a plus badge
      return (<>
        <path d="M3.5 8h12.5l-.9 11.2a1.6 1.6 0 0 1-1.6 1.4H6a1.6 1.6 0 0 1-1.6-1.4L3.5 8Z" fill={fg} opacity=".18" stroke={fg} {...S} />
        <path d="M7 10V6.6a2.8 2.8 0 0 1 5.6 0V10" stroke={fg} {...S} />
        <circle cx="17.6" cy="16.6" r="4.6" fill={fg} />
        <path d="M17.6 14.4v4.4M15.4 16.6h4.4" stroke="#fff" strokeWidth="1.6" strokeLinecap="round" />
      </>)
    case 'flyer':
      // a courier flyer (plastic mailer) with its seal strip
      return (<>
        <path d="M5 3.5h14a1 1 0 0 1 1 1v15a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1v-15a1 1 0 0 1 1-1Z" fill={fg} opacity=".18" stroke={fg} {...S} />
        <path d="M4 7.5h16" stroke={fg} {...S} />
        <path d="M6.5 5.5h11" stroke={fg} strokeWidth="1.2" strokeDasharray="1.6 1.6" strokeLinecap="round" />
        <rect x="7" y="11" width="10" height="5.5" rx="1" fill={fg} opacity=".85" />
        <path d="M8.8 13h6.4M8.8 14.8h4" stroke="#fff" strokeWidth="1.1" strokeLinecap="round" />
      </>)
    case 'sticker':
      // a label with a peeled corner and barcode bars
      return (<>
        <path d="M5 4h14a1 1 0 0 1 1 1v9.5L14.5 20H5a1 1 0 0 1-1-1V5a1 1 0 0 1 1-1Z" fill={fg} opacity=".18" stroke={fg} {...S} />
        <path d="M20 14.5h-4.5a1 1 0 0 0-1 1V20" fill={fg} stroke={fg} {...S} />
        <path d="M7.5 7.5v5M9.5 7.5v5M11 7.5v5M13 7.5v5M15.5 7.5v3.5" stroke={fg} strokeWidth="1.3" strokeLinecap="round" />
        <path d="M7.5 15.5h4.5" stroke={fg} strokeWidth="1.3" strokeLinecap="round" />
      </>)
    case 'box':
      // an open carton
      return (<>
        <path d="M4 9.5 12 13l8-3.5V18l-8 3.5L4 18V9.5Z" fill={fg} opacity=".18" stroke={fg} {...S} />
        <path d="M12 13v8.5" stroke={fg} {...S} />
        <path d="M4 9.5 2.5 6.5 10.5 3 12 6M20 9.5l1.5-3L13.5 3 12 6" stroke={fg} {...S} />
        <path d="M4 9.5 12 6l8 3.5" stroke={fg} {...S} />
      </>)
    case 'tape':
      // a roll of packing tape with its free end
      return (<>
        <circle cx="10" cy="11" r="7" fill={fg} opacity=".18" stroke={fg} {...S} />
        <circle cx="10" cy="11" r="2.8" stroke={fg} {...S} />
        <path d="M10 18h10.5v-2.2" stroke={fg} {...S} />
        <path d="M17 11.2V18" stroke={fg} {...S} />
      </>)
    case 'other':
      // a tag
      return (<>
        <path d="M3.5 12.2V4.5a1 1 0 0 1 1-1h7.7l8.3 8.3a1.4 1.4 0 0 1 0 2l-6.7 6.7a1.4 1.4 0 0 1-2 0l-8.3-8.3Z" fill={fg} opacity=".18" stroke={fg} {...S} />
        <circle cx="8" cy="8" r="1.6" fill={fg} />
      </>)
    case 'settings':
      // a bag with a gear
      return (<>
        <path d="M3.5 8h12l-.9 11.2a1.6 1.6 0 0 1-1.6 1.4H6a1.6 1.6 0 0 1-1.6-1.4L3.5 8Z" fill={fg} opacity=".18" stroke={fg} {...S} />
        <path d="M6.8 10V6.6a2.7 2.7 0 0 1 5.4 0V10" stroke={fg} {...S} />
        <circle cx="17.5" cy="16.5" r="4.4" fill={fg} />
        <circle cx="17.5" cy="16.5" r="1.5" stroke="#fff" strokeWidth="1.3" />
        <path d="M17.5 13.2v1M17.5 18.8v1M14.2 16.5h1M19.8 16.5h1" stroke="#fff" strokeWidth="1.3" strokeLinecap="round" />
      </>)
  }
}

function make(key: MaterialsIconKey) {
  const c = MATERIALS_ICON_TONE[key]
  const Mark = forwardRef<SVGSVGElement, IconProps>(function MaterialsMark({ size = 16, className, weight: _w, mirrored: _m, alt, color: _c, ...rest }, ref) {
    return (
      <svg ref={ref} width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden={alt ? undefined : true} aria-label={alt} focusable={false} className={cn('keep-color shrink-0', className)} {...rest}>
        {paths(key, c.fg)}
      </svg>
    )
  })
  Mark.displayName = `MaterialsIcon.${key}`
  return Mark
}

/** Phosphor-compatible components: `<MATERIALS_ICONS.flyer size={20} />`. */
export const MATERIALS_ICONS = Object.fromEntries((Object.keys(MATERIALS_ICON_TONE) as MaterialsIconKey[]).map((k) => [k, make(k)])) as Record<MaterialsIconKey, ReturnType<typeof make>>

/** Mark on its tinted square (cards, empty photo slots). */
export function MaterialsIconTile({ name, size = 44 }: { name: MaterialsIconKey; size?: number }) {
  const c = MATERIALS_ICON_TONE[name] ?? MATERIALS_ICON_TONE.other
  const I = MATERIALS_ICONS[name] ?? MATERIALS_ICONS.other
  return (
    <span className="inline-flex shrink-0 items-center justify-center rounded-2xl" style={{ width: size, height: size, background: c.bg }}>
      <I size={Math.round(size * 0.58)} />
    </span>
  )
}
